import React, { useMemo, useEffect, useState } from 'react';
import type { SessionLogEntry, Task, Project } from '../types';
import { getSessionLogs } from '../utils/sessionLog.ts';
import {
  totalFocusTime,
  focusByDay,
  bestStreak,
} from '../utils/stats.ts';
import { formatDuration } from '../utils/formatDuration.ts';
import { useTask } from '../contexts/TaskContext.tsx';
import './StatsModal.css';

interface StatsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ---------- Helpers ----------

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoWeekDay(d: Date): number {
  // Returns 0=Mon … 6=Sun (ISO week day, 0-based)
  return (d.getDay() + 6) % 7;
}

function relativeDate(startTime: number): string {
  const d = new Date(startTime);
  const today = toDateKey(new Date());
  const dStr = toDateKey(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (dStr === today) return `Today, ${hh}:${mm}`;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (dStr === toDateKey(yesterday)) return `Yesterday, ${hh}:${mm}`;
  return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}, ${hh}:${mm}`;
}

// ---------- Sub-components ----------

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}

function StatCard({ label, value, sub, accent }: StatCardProps) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={accent ? { color: accent } : undefined}>{value}</div>
      {sub && <div className="stat__sub">{sub}</div>}
    </div>
  );
}

// 26-week heatmap: 26 columns × 7 rows, most recent week on the right.
interface HeatmapProps {
  byDay: Record<string, number>;
}

const HEAT_PALETTE = [
  'var(--kf-surface-2)',        // 0 min
  'oklch(80% 0.04 254)',        // 1–29 min
  'oklch(64% 0.10 254)',        // 30–59 min
  'oklch(54% 0.13 254)',        // 60–119 min
  'oklch(45% 0.15 254)',        // 120+ min
];

function heatLevel(minutes: number): number {
  if (minutes === 0) return 0;
  if (minutes < 30) return 1;
  if (minutes < 60) return 2;
  if (minutes < 120) return 3;
  return 4;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ActivityHeatmap({ byDay }: HeatmapProps) {
  // Build an array of 26×7 = 182 cells, ending on the most recent Sunday.
  const today = new Date();
  // End of grid = end of current week (Sunday in ISO: day 6)
  const endOfGrid = new Date(today);
  const daysUntilSunday = (6 - isoWeekDay(today) + 7) % 7;
  endOfGrid.setDate(endOfGrid.getDate() + daysUntilSunday);

  // Build 182 days from start to end
  const startOfGrid = new Date(endOfGrid);
  startOfGrid.setDate(startOfGrid.getDate() - 182 + 1);

  const cells: { date: string; minutes: number }[] = [];
  for (let i = 0; i < 182; i++) {
    const d = new Date(startOfGrid);
    d.setDate(d.getDate() + i);
    const key = toDateKey(d);
    cells.push({ date: key, minutes: byDay[key] ?? 0 });
  }

  // Month labels: find the first cell in each month and record which column it falls in
  const monthLabels: { label: string; col: number }[] = [];
  let lastMonth = -1;
  for (let i = 0; i < 182; i++) {
    const d = new Date(startOfGrid);
    d.setDate(d.getDate() + i);
    const m = d.getMonth();
    if (m !== lastMonth) {
      monthLabels.push({ label: MONTH_ABBR[m], col: Math.floor(i / 7) });
      lastMonth = m;
    }
  }

  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="heatmap-wrap">
      {/* Month labels row */}
      <div className="heatmap-months">
        {monthLabels.map(({ label, col }) => (
          <span key={`${label}-${col}`} className="heatmap-month" style={{ gridColumn: col + 1 }}>
            {label}
          </span>
        ))}
      </div>

      <div className="heatmap-body">
        {/* Day-of-week labels */}
        <div className="heatmap-days">
          {DAY_LABELS.map((d, i) => (
            <span key={d} className="heatmap-day" style={{ display: i % 2 === 0 ? 'block' : 'none' }}>
              {d}
            </span>
          ))}
        </div>

        {/* Grid: 26 columns × 7 rows */}
        <div className="heat">
          {Array.from({ length: 26 }, (_, weekIdx) =>
            Array.from({ length: 7 }, (_, dayIdx) => {
              const cellIdx = weekIdx * 7 + dayIdx;
              const { date, minutes } = cells[cellIdx];
              const level = heatLevel(minutes);
              return (
                <div
                  key={date}
                  className="heat__cell"
                  style={{ background: HEAT_PALETTE[level] }}
                  title={`${date} — ${minutes}m`}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="legend">
        <span className="muted">Less</span>
        <span className="legend__dots">
          {HEAT_PALETTE.map((color, i) => (
            <i key={i} style={{ background: color }} />
          ))}
        </span>
        <span className="muted">More</span>
      </div>
    </div>
  );
}

// 7-day bar chart (Mon–Sun for the current ISO week)
interface WeekBarsProps {
  byDay: Record<string, number>;
}

const WEEK_DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function WeekBars({ byDay }: WeekBarsProps) {
  const today = new Date();
  const todayKey = toDateKey(today);
  const currentDayOfWeek = isoWeekDay(today); // 0=Mon…6=Sun

  // Build Mon–Sun for the current week
  const weekDays = WEEK_DAYS_SHORT.map((label, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - currentDayOfWeek + i);
    const key = toDateKey(d);
    return { label, key, minutes: byDay[key] ?? 0 };
  });

  const maxMinutes = Math.max(...weekDays.map(d => d.minutes), 1);
  const totalMinutes = weekDays.reduce((s, d) => s + d.minutes, 0);
  const totalHours = (totalMinutes / 60).toFixed(1);

  return (
    <>
      <div className="stats__card-head">
        <h3>This week</h3>
        <span className="muted">{totalHours}h focused</span>
      </div>
      <div className="weekbars">
        {weekDays.map(({ label, key, minutes }) => {
          const isToday = key === todayKey;
          const heightPct = (minutes / maxMinutes) * 100;
          return (
            <div className="weekbar" key={key}>
              <div className="weekbar__col">
                <div
                  className="weekbar__fill"
                  style={{
                    height: `${heightPct}%`,
                    background: isToday ? 'var(--kf-work)' : 'var(--kf-surface-2)',
                  }}
                />
              </div>
              <div className="weekbar__day">{label}</div>
              <div className="weekbar__h">{minutes > 0 ? `${minutes}m` : '—'}</div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// By-project horizontal bars (top 4)
interface ProjectBarsProps {
  tasks: Task[];
  projects: Project[];
}

function ProjectBars({ tasks, projects }: ProjectBarsProps) {
  const projectColors = [
    'var(--kf-work)',
    'var(--kf-break)',
    'var(--kf-long-break)',
    'var(--kf-muted)',
  ];

  const projectTotals = useMemo(() => {
    const map: Record<string, { name: string; seconds: number }> = {};

    tasks.forEach(task => {
      const key = task.projectId ?? '__none__';
      const name = task.projectId
        ? (projects.find(p => p.id === task.projectId)?.name ?? 'Unknown')
        : 'General';
      if (!map[key]) map[key] = { name, seconds: 0 };
      map[key].seconds += task.totalTimeSpent;
    });

    return Object.values(map)
      .filter(p => p.seconds > 0)
      .sort((a, b) => b.seconds - a.seconds)
      .slice(0, 4);
  }, [tasks, projects]);

  const maxSeconds = Math.max(...projectTotals.map(p => p.seconds), 1);

  if (projectTotals.length === 0) {
    return (
      <div className="bars">
        <span className="muted">No project data yet.</span>
      </div>
    );
  }

  return (
    <div className="bars">
      {projectTotals.map((proj, i) => {
        const pct = Math.round((proj.seconds / maxSeconds) * 100);
        const hours = (proj.seconds / 3600).toFixed(1);
        const color = projectColors[i % projectColors.length];
        return (
          <div className="bar" key={proj.name}>
            <div className="bar__head">
              <span>
                <i className="bar__pip" style={{ background: color }} />
                {proj.name}
              </span>
              <span className="bar__hrs">{hours}h</span>
            </div>
            <div className="bar__track">
              <div className="bar__fill" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Recent sessions table (last 5)
interface RecentSessionsProps {
  logs: SessionLogEntry[];
  tasks: Task[];
  projects: Project[];
}

function RecentSessions({ logs, tasks, projects }: RecentSessionsProps) {
  const recent = useMemo(() => {
    return [...logs]
      .filter(e => e.sessionType === 'pomodoro')
      .sort((a, b) => b.startTime - a.startTime)
      .slice(0, 5);
  }, [logs]);

  if (recent.length === 0) {
    return <span className="muted">No sessions recorded yet.</span>;
  }

  return (
    <table className="sessions">
      <thead>
        <tr>
          <th>Task</th>
          <th>Project</th>
          <th>Duration</th>
          <th>When</th>
        </tr>
      </thead>
      <tbody>
        {recent.map(entry => {
          // sessionId may match a task session — look up by matching sessionId in task sessions
          const matchedTask = tasks.find(t => t.sessions?.some(s => s.sessionId === entry.sessionId));
          const taskName = matchedTask?.text ?? '—';
          const projectName = matchedTask?.projectId
            ? (projects.find(p => p.id === matchedTask.projectId)?.name ?? '—')
            : '—';
          const dur = formatDuration(entry.actualDuration) || `${Math.round(entry.actualDuration / 60)}m`;
          return (
            <tr key={entry.sessionId}>
              <td>{taskName}</td>
              <td>{projectName !== '—' ? <span className="chip">{projectName}</span> : <span className="muted">—</span>}</td>
              <td className="num">{dur}</td>
              <td className="muted">{relativeDate(entry.startTime)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------- Main component ----------

const StatsModal: React.FC<StatsModalProps> = ({ isOpen, onClose }) => {
  const { tasks, projects } = useTask();
  const [logs, setLogs] = useState<SessionLogEntry[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setLogs(getSessionLogs());
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'stats_opened', { event_category: 'pomodoro_timer' });
    }
  }, [isOpen]);

  const today = toDateKey(new Date());

  const allTimeFocus = useMemo(() => totalFocusTime(logs), [logs]);
  const best = useMemo(() => bestStreak(logs), [logs]);
  const byDay = useMemo(() => focusByDay(logs, 182), [logs]);

  const pomodoroLogs = useMemo(() => logs.filter(e => e.sessionType === 'pomodoro'), [logs]);
  const completionRate = useMemo(() => {
    if (pomodoroLogs.length === 0) return 0;
    const completed = pomodoroLogs.filter(e => e.completionStatus === 'completed').length;
    return Math.round((completed / pomodoroLogs.length) * 100);
  }, [pomodoroLogs]);

  const handleExportCSV = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'export_csv', { event_category: 'pomodoro_timer' });
    }
    const header = 'date,sessionType,startTime,endTime,plannedDuration,actualDuration,completionStatus';
    const rows = logs.map(e =>
      `${e.date},${e.sessionType},${e.startTime},${e.endTime},${e.plannedDuration},${e.actualDuration},${e.completionStatus}`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kairofocus-sessions-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const totalFocusFormatted = formatDuration(allTimeFocus) || '0m';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="stats-modal" onClick={e => e.stopPropagation()}>
        {/* Modal close button */}
        <button className="stats-modal__close" onClick={onClose} aria-label="Close stats">×</button>

        <div className="stats-modal__scroll">
          <div className="stats stats--dashboard">
            {/* Header */}
            <header className="stats__head">
              <div>
                <div className="stats__eyebrow">FOCUS · LAST 6 MONTHS</div>
                <h1 className="stats__title">Focus, in retrospect</h1>
              </div>
            </header>

            {/* Summary cards — 4 columns */}
            <div className="stats__grid">
              <StatCard
                label="Total focus"
                value={totalFocusFormatted}
                accent="var(--kf-work)"
              />
              <StatCard
                label="Sessions"
                value={String(pomodoroLogs.length)}
                sub="pomodoros total"
              />
              <StatCard
                label="Best streak"
                value={`${best}d`}
                accent="var(--kf-long-break)"
              />
              <StatCard
                label="Completion"
                value={`${completionRate}%`}
                sub="sessions finished"
              />
            </div>

            {/* Activity heatmap */}
            <section className="stats__card">
              <div className="stats__card-head">
                <h3>Activity</h3>
              </div>
              <ActivityHeatmap byDay={byDay} />
            </section>

            {/* Two-column row: by-project + 7-day bar chart */}
            <div className="stats__row">
              <section className="stats__card">
                <div className="stats__card-head">
                  <h3>By project</h3>
                  <span className="muted">All time</span>
                </div>
                <ProjectBars tasks={tasks} projects={projects} />
              </section>

              <section className="stats__card">
                <WeekBars byDay={byDay} />
              </section>
            </div>

            {/* Recent sessions */}
            <section className="stats__card">
              <div className="stats__card-head">
                <h3>Recent sessions</h3>
                <button className="stats-modal__export link" onClick={handleExportCSV}>
                  Export CSV
                </button>
              </div>
              <RecentSessions logs={logs} tasks={tasks} projects={projects} />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsModal;
